<template>
  <!-- Bootstrap のモーダルは使わない（BS5 と AdminLTE 3.1 が混在し data-dismiss などが効かないため）。
       store/PaidLeavePage.vue の管理者代理作成と同じ v-if のオーバーレイ -->
  <div class="tce-overlay" @click.self="$emit('close')">
    <div class="tce-box" role="dialog" aria-modal="true">
      <div class="tce-header">
        <h6 class="tce-title">
          <i :class="['fas', isEdit ? 'fa-pen' : 'fa-plus', 'mr-2']"></i>{{ isEdit ? '打刻を修正' : '打刻を追加' }}
        </h6>
        <button type="button" class="tce-close" @click="$emit('close')">&times;</button>
      </div>

      <div class="tce-body">
        <div class="tce-field">
          <label>スタッフ <span v-if="!isEdit" class="required">*</span></label>
          <div v-if="isEdit || staffOptions.length === 1" class="tce-static">{{ staffName }}</div>
          <select v-else v-model="form.staff_id" class="form-select" name="staff_id" @change="applyDefaultBreak">
            <option value="">選択してください</option>
            <option v-for="s in staffOptions" :key="s.id" :value="s.id">{{ s.name }}</option>
          </select>
          <div v-if="errors.staff_id" class="tce-error">{{ errors.staff_id[0] }}</div>
        </div>

        <div class="tce-field">
          <label>店舗 <span v-if="!isEdit" class="required">*</span></label>
          <div v-if="isEdit || shopOptions.length === 1" class="tce-static">{{ shopName }}</div>
          <select v-else v-model="form.shop_id" class="form-select" name="shop_id">
            <option value="">選択してください</option>
            <option v-for="s in shopOptions" :key="s.id" :value="s.id">{{ s.shop_name }}</option>
          </select>
          <div v-if="errors.shop_id" class="tce-error">{{ errors.shop_id[0] }}</div>
        </div>

        <div class="tce-field">
          <label>日付 <span class="required">*</span></label>
          <input type="date" v-model="form.date" class="form-control" name="date">
          <div v-if="errors.date" class="tce-error">{{ errors.date[0] }}</div>
        </div>

        <div class="tce-field-row">
          <div class="tce-field">
            <label>出勤 <span class="required">*</span></label>
            <input type="time" v-model="form.time_start" class="form-control" name="time_start">
            <div v-if="errors.time_start" class="tce-error">{{ errors.time_start[0] }}</div>
          </div>
          <div class="tce-field">
            <label>退勤 <span class="required">*</span><span v-if="isOvernight" class="tce-overnight">（翌日）</span></label>
            <input type="time" v-model="form.time_stop" class="form-control" name="time_stop">
            <div v-if="errors.time_stop" class="tce-error">{{ errors.time_stop[0] }}</div>
          </div>
        </div>

        <div class="tce-field">
          <label>休憩 <span class="required">*</span></label>
          <div class="tce-break">
            <select v-model.number="form.break_hours" class="form-select" name="break_hours">
              <option v-for="h in 13" :key="h" :value="h - 1">{{ h - 1 }}</option>
            </select>
            <span>時間</span>
            <select v-model.number="form.break_minutes" class="form-select" name="break_minutes">
              <option v-for="m in 60" :key="m" :value="m - 1">{{ String(m - 1).padStart(2, '0') }}</option>
            </select>
            <span>分</span>
          </div>
          <div v-if="errors.break_time" class="tce-error">{{ errors.break_time[0] }}</div>
        </div>

        <div class="tce-field">
          <label>備考</label>
          <textarea v-model="form.notes" class="form-control" rows="2" maxlength="255" name="notes" placeholder="任意"></textarea>
          <div v-if="errors.notes" class="tce-error">{{ errors.notes[0] }}</div>
        </div>

        <p v-if="isInProgress" class="tce-warning">
          <i class="fas fa-exclamation-triangle mr-1"></i>退勤していない打刻です。保存すると、入力した時刻で退勤したことになります。スタッフがこの日にもう一度出勤打刻をすると、別の打刻が作られます。
        </p>
        <p v-if="stopDateOutOfRange" class="tce-warning">
          <i class="fas fa-exclamation-triangle mr-1"></i>この打刻の退勤（{{ stopDateOutOfRangeLabel }}）は出勤から24時間以上後です。保存すると退勤は出勤日または翌日の時刻になり、24時間以上のままでは保存できません。退勤を確認して入力し直してください。
        </p>
        <p v-if="startDateShiftedByRounding" class="tce-warning">
          <i class="fas fa-exclamation-triangle mr-1"></i>実際の出勤の打刻は {{ formatMonthDayTime(timecard.raw_time_start) }} です（丸めて {{ formatMonthDayTime(timecard.rounded_time_start) }} と表示しています）。出勤と日付を変えずに保存すれば、元の打刻のまま残ります。出勤を {{ formatMonthDay(timecard.raw_time_start) }} の時刻に直すときは、日付も {{ formatMonthDay(timecard.raw_time_start) }} に変えてください。
        </p>
        <p class="tce-note">※ 退勤が出勤より前の時刻なら翌日の退勤になります。保存すると<strong>承認済み</strong>になります。</p>

        <div v-if="isEdit" class="tce-history">
          <div class="tce-history-title"><i class="fas fa-history mr-1"></i>変更履歴</div>
          <p class="tce-history-note">管理者による登録・修正だけが載ります（スタッフ本人の修正申請や休憩の変更は載りません）。</p>
          <div v-if="!historiesLoaded" class="tce-history-empty">読み込み中…</div>
          <div v-else-if="histories.length === 0" class="tce-history-empty">履歴はありません</div>
          <ul v-else class="tce-history-list">
            <li v-for="(h, i) in histories" :key="i" class="tce-history-item">
              <div class="tce-history-head">
                <span class="tce-history-action">{{ h.action_label }}</span>
                <span>{{ h.created_at }}</span>
                <span>{{ h.user_name || '（不明なユーザー）' }}</span>
              </div>
              <div v-for="c in h.changes" :key="c.label" class="tce-history-change">{{ c.label }}: {{ c.before }} → {{ c.after }}</div>
            </li>
          </ul>
        </div>
      </div>

      <div class="tce-footer">
        <button type="button" class="btn btn-outline-secondary btn-sm" @click="$emit('close')">キャンセル</button>
        <button type="button" class="btn btn-success btn-sm tce-submit" :disabled="!canSubmit" @click="submit">
          <i class="fas fa-check mr-1"></i>保存
        </button>
      </div>
    </div>
  </div>
</template>

<script>
const pad = (n) => String(n).padStart(2, '0');

export default {
  name: 'TimecardEditModal',
  props: {
    // 'create'（打刻を追加）/ 'edit'（修正）
    mode: { type: String, required: true },
    // 修正する打刻。一覧 API の打刻（丸めた値 rounded_* と丸める前の値 raw_* を含む）
    timecard: { type: Object, default: null },
    // スタッフの候補 [{ id, name, break_time }]。break_time は分で、新規の休憩の初期値に使う
    staffOptions: { type: Array, default: () => [] },
    // 店舗の候補 [{ id, shop_name }]
    shopOptions: { type: Array, default: () => [] },
    // 新規の日付の初期値 'YYYY-MM-DD'
    defaultDate: { type: String, default: '' },
  },
  emits: ['close', 'saved'],
  data() {
    return {
      form: { staff_id: '', shop_id: '', date: '', time_start: '', time_stop: '', break_hours: 0, break_minutes: 0, notes: '' },
      errors: {},
      saving: false,
      histories: [],
      historiesLoaded: false,
    };
  },
  computed: {
    isEdit() {
      return this.mode === 'edit';
    },
    staffName() {
      const id = this.isEdit ? this.timecard.staff_id : this.form.staff_id;
      const staff = this.staffOptions.find(s => Number(s.id) === Number(id));
      return staff ? staff.name : '';
    },
    shopName() {
      if (this.isEdit && this.timecard.shop && this.timecard.shop.shop_name) return this.timecard.shop.shop_name;
      const id = this.isEdit ? this.timecard.shop_id : this.form.shop_id;
      const shop = this.shopOptions.find(s => Number(s.id) === Number(id));
      return shop ? shop.shop_name : '';
    },
    // 退勤が出勤より前なら翌日の退勤（サーバーの TimecardPeriod と同じ規則）
    isOvernight() {
      return !!(this.form.time_start && this.form.time_stop && this.form.time_stop < this.form.time_start);
    },
    // 退勤していない打刻（勤務中、または退勤の打ち忘れ）
    isInProgress() {
      return this.isEdit && !this.timecard.raw_time_stop;
    },
    canSubmit() {
      if (this.saving) return false;
      if (!this.form.date || !this.form.time_start || !this.form.time_stop) return false;
      if (!this.isEdit && (!this.form.staff_id || !this.form.shop_id)) return false;
      return true;
    },
    // 出勤日の2日以上後に退勤している異常な打刻（休憩だけ直しても退勤日がずれて保存される）
    // 保存済みの勤務が 24 時間以上（退勤が出勤日の2日以上後を含む）。フォームの退勤は時刻だけで、
    // 保存すると出勤日か翌日の時刻になるうえ、24 時間以上のままでは保存できないので注意書きを出す
    stopDateOutOfRange() {
      if (!this.isEdit || !this.timecard.raw_time_stop || !this.timecard.raw_time_start) return false;
      // 'YYYY-MM-DDTHH:MM:SS'（オフセットなし）はローカル時刻として解釈される
      const start = new Date(this.timecard.raw_time_start.replace(' ', 'T'));
      const stop = new Date(this.timecard.raw_time_stop.replace(' ', 'T'));
      if (isNaN(start.getTime()) || isNaN(stop.getTime())) return false;
      return stop.getTime() - start.getTime() >= 24 * 60 * 60 * 1000;
    },
    // 上の警告文に出す「M/D HH:MM」表記
    stopDateOutOfRangeLabel() {
      return this.timecard && this.timecard.raw_time_stop ? this.formatMonthDayTime(this.timecard.raw_time_stop) : '';
    },
    // 丸めで出勤の日付がずれて表示される打刻（23:53 → 翌日 0:00）。日付欄が実際の打刻日と違うので、
    // 「日付の表示ミス」と思って日付を直されないよう注意書きを出す
    startDateShiftedByRounding() {
      if (!this.isEdit) return false;
      const raw = this.timecard.raw_time_start;
      const rounded = this.timecard.rounded_time_start;
      return !!(raw && rounded && raw.slice(0, 10) !== rounded.slice(0, 10));
    },
  },
  watch: {
    // 打刻を追加時、候補が親の非同期取得（loadData()/loadShops()）で遅れて届いても
    // 単一候補なら自動選択する（mounted() 時点では候補が空でスキップされているため）
    staffOptions(list) {
      if (!this.isEdit && list.length === 1 && !this.form.staff_id) {
        this.form.staff_id = list[0].id;
        this.applyDefaultBreak();
      }
    },
    shopOptions(list) {
      if (!this.isEdit && list.length === 1 && !this.form.shop_id) {
        this.form.shop_id = list[0].id;
      }
    },
  },
  mounted() {
    if (this.isEdit) {
      // 初期値は一覧と同じ丸めた値（日付付き）。触らずに保存した項目は、サーバーが
      // 元の打刻（raw_*）に戻すので、実際の打刻時刻は消えない（AdminTimecardController::update）。
      // rounded_* が無い（API が古い、退勤が24時間超で丸めの計算から外れた）ときは丸める前の値を出す
      const start = this.timecard.rounded_time_start || this.timecard.raw_time_start || '';
      const stop = this.timecard.rounded_time_stop || this.timecard.raw_time_stop || '';
      const brk = (this.timecard.rounded_basic_break_hours || this.timecard.raw_basic_break_hours || '00:00:00').split(':');
      this.form.date = start.slice(0, 10);
      this.form.time_start = start.slice(11, 16);
      this.form.time_stop = stop.slice(11, 16);
      this.form.break_hours = parseInt(brk[0], 10) || 0;
      this.form.break_minutes = parseInt(brk[1], 10) || 0;
      this.form.notes = this.timecard.notes || '';
      this.loadHistories();
    } else {
      this.form.date = this.defaultDate;
      if (this.staffOptions.length === 1) this.form.staff_id = this.staffOptions[0].id;
      if (this.shopOptions.length === 1) this.form.shop_id = this.shopOptions[0].id;
      this.applyDefaultBreak();
    }
  },
  methods: {
    // 'YYYY-MM-DD HH:MM:SS' → 'M/D'
    formatMonthDay(value) {
      return this.formatMonthDayTime(value).split(' ')[0];
    },
    // 'YYYY-MM-DD HH:MM:SS' → 'M/D HH:MM'
    formatMonthDayTime(value) {
      if (!value) return '';
      const [datePart, timePart] = value.split(' ');
      const [, m, d] = datePart.split('-');
      return `${Number(m)}/${Number(d)} ${(timePart || '').slice(0, 5)}`;
    },
    // 新規の休憩は、スタッフの休憩時間（staff.break_time、分）を初期値にする
    applyDefaultBreak() {
      const staff = this.staffOptions.find(s => Number(s.id) === Number(this.form.staff_id));
      const minutes = staff && staff.break_time ? Number(staff.break_time) : 0;
      this.form.break_hours = Math.min(Math.floor(minutes / 60), 12);
      this.form.break_minutes = minutes % 60;
    },
    loadHistories() {
      this.$http.get(process.env.VUE_APP_API_PATH + '/company/timecard/admin/' + this.timecard.id + '/histories')
        .then(response => { this.histories = response.data.data || []; })
        .catch(error => { console.log(error); })
        .finally(() => { this.historiesLoaded = true; });
    },
    submit() {
      this.saving = true;
      this.errors = {};
      const payload = {
        date: this.form.date,
        time_start: this.form.time_start,
        time_stop: this.form.time_stop,
        break_time: pad(this.form.break_hours) + ':' + pad(this.form.break_minutes),
        notes: this.form.notes,
      };
      const base = process.env.VUE_APP_API_PATH + '/company/timecard/admin';
      const request = this.isEdit
        ? this.$http.put(base + '/' + this.timecard.id, payload)
        : this.$http.post(base, { ...payload, staff_id: this.form.staff_id, shop_id: this.form.shop_id });
      request
        .then(response => {
          this.$toast.success(response.data.message);
          this.$emit('saved');
        })
        .catch(error => {
          const res = error.response;
          if (res && res.status === 422 && res.data && res.data.errors) {
            // 項目ごとに入力欄の下へ出す（Laravel の既定の message は英語なので使わない）
            this.errors = res.data.errors;
          } else {
            this.$toast.error((res && res.data && res.data.message) || '保存に失敗しました');
          }
        })
        .finally(() => { this.saving = false; });
    },
  },
};
</script>

<style scoped>
.tce-overlay {
  position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0, 0, 0, 0.45);
  z-index: 1060; display: flex; align-items: center; justify-content: center;
}
.tce-box {
  background: #fff; border-radius: 12px; width: 520px; max-width: 95vw; max-height: 90vh;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25); display: flex; flex-direction: column; overflow: hidden;
}
.tce-header { display: flex; align-items: center; justify-content: space-between; padding: 14px 20px; border-bottom: 1px solid #e2e8f0; }
.tce-title { font-weight: 700; font-size: 16px; margin: 0; color: #1a202c; }
.tce-close { border: none; background: none; font-size: 24px; color: #718096; cursor: pointer; padding: 0 4px; line-height: 1; }
.tce-close:hover { color: #e53e3e; }
.tce-body { padding: 16px 20px; overflow-y: auto; }
.tce-footer { padding: 12px 20px; border-top: 1px solid #e2e8f0; display: flex; justify-content: flex-end; gap: 8px; }

.tce-field { margin-bottom: 12px; }
.tce-field label { font-size: 13px; font-weight: 600; color: #4a5568; display: block; margin-bottom: 6px; }
.tce-field-row { display: flex; gap: 12px; }
.tce-field-row .tce-field { flex: 1; }
.tce-static { font-size: 14px; color: #1a202c; padding: 4px 0; }
.tce-break { display: flex; align-items: center; gap: 6px; }
.tce-break .form-select { width: auto; }
.tce-overnight { color: #d69e2e; font-weight: 700; margin-left: 4px; }
.required { color: #e53e3e; font-weight: 700; }
.tce-error { color: #e53e3e; font-size: 12px; margin-top: 4px; }
.tce-note { font-size: 12px; color: #718096; margin: 8px 0 0 0; }
.tce-warning { font-size: 12px; color: #744210; background: #fefcbf; border-radius: 6px; padding: 8px 10px; margin: 8px 0 0 0; }

.tce-history { margin-top: 16px; border-top: 1px solid #e2e8f0; padding-top: 12px; }
.tce-history-title { font-size: 13px; font-weight: 700; color: #2d3748; }
.tce-history-note { font-size: 11px; color: #718096; margin: 4px 0 8px 0; }
.tce-history-empty { font-size: 12px; color: #a0aec0; }
.tce-history-list { list-style: none; padding: 0; margin: 0; }
.tce-history-item { border: 1px solid #edf2f7; border-radius: 6px; padding: 6px 10px; margin-bottom: 6px; font-size: 12px; }
.tce-history-head { display: flex; gap: 8px; color: #4a5568; margin-bottom: 2px; }
.tce-history-action { font-weight: 700; color: #2b6cb0; }
.tce-history-change { color: #1a202c; padding-left: 4px; }
</style>
